import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/apiBase';

const CartContext = createContext();

const API = API_BASE;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total_items: 0 });
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    // Generate or get session ID
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('session_id', sid);
    }
    setSessionId(sid);
    fetchCart(sid);
  }, []);

  const fetchCart = async (sid) => {
    try {
      const response = await axios.get(`${API}/cart/${sid}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      await axios.post(`${API}/cart/add`, {
        session_id: sessionId,
        product_id: productId,
        quantity
      });
      await fetchCart(sessionId);
      toast.success('Added to cart!');
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
      return false;
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      await axios.put(`${API}/cart/update`, {
        session_id: sessionId,
        product_id: productId,
        quantity
      });
      await fetchCart(sessionId);
      toast.success('Cart updated!');
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error('Failed to update cart');
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${sessionId}/item/${productId}`);
      await fetchCart(sessionId);
      toast.success('Removed from cart');
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart({ items: [], total_items: 0 });
      toast.success('Cart cleared');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
    }
  };

  const value = {
    cart,
    sessionId,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart: () => fetchCart(sessionId)
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
